import React, { useState } from 'react';
import { X, AlertCircle, Check, Clock, Award, Users, Calendar } from 'lucide-react';

const ClientSubmissionEditModal = ({ isOpen, onClose, submission, onUpdate }) => {
  const [status, setStatus] = useState(submission?.status || submission?.current_status || '');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen || !submission) return null;

  // Client submission status flow for Sales Managers (aligned with Flow Management)
  const clientSubmissionStages = [
    { value: 'submitted', label: 'Submitted', color: 'bg-blue-100 text-blue-800', icon: Clock },
    { value: 'rejected_pre_interview', label: 'Rejected (Pre-Interview)', color: 'bg-red-100 text-red-800', icon: X },
    { value: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-purple-100 text-purple-800', icon: Calendar },
    { value: 'interviewed', label: 'Interviewed', color: 'bg-indigo-100 text-indigo-800', icon: Check },
    { value: 'rejected_post_interview', label: 'Rejected (Post-Interview)', color: 'bg-red-100 text-red-800', icon: X },
    { value: 'offer_made', label: 'Offer Made', color: 'bg-green-100 text-green-800', icon: Award },
    { value: 'offer_accepted', label: 'Offer Accepted', color: 'bg-green-100 text-green-800', icon: Check },
    { value: 'offer_rejected', label: 'Offer Rejected', color: 'bg-orange-100 text-orange-800', icon: X },
    { value: 'onboarding', label: 'Onboarding', color: 'bg-indigo-100 text-indigo-800', icon: Users },
    { value: 'onboarded', label: 'Onboarded (Final)', color: 'bg-indigo-100 text-indigo-800', icon: Check },
    { value: 'started', label: 'Started', color: 'bg-teal-100 text-teal-800', icon: Check }
  ];

  const handleUpdate = async () => {
    if (!status) {
      alert('Please select a status');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/submissions/${submission.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            new_status: status,
            status_change_notes: notes,
            updated_at: new Date().toISOString()
          })
        }
      );

      if (response.ok) {
        const updatedSubmission = await response.json();
        onUpdate(updatedSubmission);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to update submission:', errorData);
        alert(`Failed to update submission status: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating submission:', error);
      alert('Error updating submission: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStage = clientSubmissionStages.find(s => s.value === status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex justify-between items-start">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white mb-1">Edit Client Submission Status</h2>
            <p className="text-blue-100 text-sm">Update submission status based on client feedback and interview outcomes</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Submission Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Candidate</p>
                  <p className="font-semibold text-gray-900">{submission.candidate_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Job Title</p>
                  <p className="font-semibold text-gray-900">{submission.job_title}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Client</p>
                  <p className="font-semibold text-gray-900">{submission.client_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Submitted By</p>
                  <p className="font-semibold text-gray-900">{submission.submitted_by_name || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Current Status */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Current Status</h3>
              {getCurrentStage && (
                <div className="flex items-center gap-3">
                  <div className={`px-4 py-2 rounded-lg ${getCurrentStage.color} font-medium`}>
                    {getCurrentStage.label}
                  </div>
                </div>
              )}
            </div>

            {/* Status Selection */}
            <div className="border border-gray-200 rounded-lg p-4">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Update Status <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4">
                Select the next stage in the client submission process
              </p>
              <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
                {clientSubmissionStages.map((stage) => (
                  <label
                    key={stage.value}
                    className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      status === stage.value
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={stage.value}
                      checked={status === stage.value}
                      onChange={(e) => setStatus(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${stage.color}`}>
                        {stage.label}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="border border-gray-200 rounded-lg p-4">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Update Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this status change..."
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
              />
            </div>

            {/* Info Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900 text-sm mb-1">Sales Manager Permissions</h4>
                  <p className="text-sm text-blue-800">
                    You can update the submission status through the client process stages. 
                    The final success state is <strong>"Onboarded"</strong> followed by <strong>"Started"</strong>. 
                    Rejections can occur at two points: <strong>"Rejected (Pre-Interview)"</strong> after submission, 
                    or <strong>"Rejected (Post-Interview)"</strong> after the interview. 
                    <strong>"Offer Rejected"</strong> occurs when candidate declines the offer. 
                    Note: Sales managers cannot withdraw submissions - only recruiters can.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={loading || !status}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Updating...</span>
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                <span>Update Status</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientSubmissionEditModal;
